import {
  autoUpdate,
  flip,
  FloatingTree,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingTree,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import React, {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';

import { DropdownMenu } from './DropdownMenu';

const Container = styled.div`
  position: relative;
  display: inline-block;
  height: fit-content;
`;

export interface DropdownProps {
  toggler: (
    ref: ((instance: HTMLElement | null) => void) | null,
    props: Record<string, unknown>
  ) => React.ReactElement;
  children: React.ReactNode;
  onClick?: (open: boolean) => void;
  open?: boolean;
  placement?: Placement;
}
/**
 * A dropdown component. The first child component inside the Dropdown component is the toggle for opening and closing the dropdown menu.
 *
 * ```ts
 *
 * import { Dropdown, DropdownItem } from '@ottatech/design'
 *
 * ```
 */
export const DropdownComponent = forwardRef<HTMLElement, DropdownProps>(
  (
    {
      toggler,
      children,
      onClick,
      open,
      placement: preferredPlacement = 'bottom-start',
      ...rest
    },
    forwardedRef
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const openState = open === undefined ? isOpen : open;
    const handleClick = useCallback(
      (open: boolean) => {
        if (onClick) {
          onClick(open);
        } else {
          setIsOpen(open);
        }
      },
      [onClick]
    );

    const { context, x, y, refs, strategy } = useFloating({
      open: openState,
      onOpenChange: handleClick,
      placement: preferredPlacement,
      middleware: [offset(10), flip(), shift()],
      whileElementsMounted: autoUpdate,
    });

    const { getReferenceProps, getFloatingProps, getItemProps } =
      useInteractions([
        useClick(context),
        useDismiss(context),
        useRole(context, {
          role: 'menu',
        }),
      ]);

    const tree = useFloatingTree();

    useEffect(() => {
      function handleTreeClick() {
        handleClick(false);
      }
      tree?.events.on('click', handleTreeClick);
      return () => {
        tree?.events.off('click', handleTreeClick);
      };
    }, [handleClick, tree]);

    const referenceRef = useMergeRefs([refs.setReference, forwardedRef]);

    return (
      <Container {...rest}>
        {toggler(referenceRef, getReferenceProps())}
        {openState && (
          <DropdownMenu
            ref={refs.setFloating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
            }}
            {...getFloatingProps()}
          >
            {Children.map(
              children,
              (child) =>
                isValidElement(child) &&
                cloneElement(
                  child,
                  getItemProps({
                    role: 'menuitem',
                    onClick(event) {
                      child.props.onClick?.(event);
                      tree?.events.emit('click');
                    },
                  })
                )
            )}
          </DropdownMenu>
        )}
      </Container>
    );
  }
);
DropdownComponent.displayName = 'DropdownComponent';

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  (props, ref) => {
    return (
      <FloatingTree>
        <DropdownComponent {...props} ref={ref} />
      </FloatingTree>
    );
  }
);
Dropdown.displayName = 'Dropdown';
